import React, { type FunctionComponent, useMemo } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { css } from '@emotion/react'
import { useForm } from 'react-hook-form'
import cloneDeep from 'lodash/cloneDeep'
import omit from 'lodash/omit'
import get from 'lodash/get'
import set from 'lodash/set'
import setWith from 'lodash/setWith'
import moment from 'moment'
import { HeroBannerFormContentText } from 'domains/merchandising/components/hero-banner-form/components/content-text/HeroBannerFormContentText'
import { HeroBannerFormLocation } from 'domains/merchandising/components/hero-banner-form/components/location/HeroBannerFormLocation'
import { HeroBannerFormLinkDestination } from 'domains/merchandising/components/hero-banner-form/components/link-destination/HeroBannerFormLinkDestination'
import { HeroBannerFormDetail } from 'domains/merchandising/components/hero-banner-form/components/detail/HeroBannerFormDetail'
import { HeroBannerFormContentImage } from 'domains/merchandising/components/hero-banner-form/components/content-image/HeroBannerFormContentImage'
import { MerchandisingFormLocale } from 'domains/merchandising/components/form-locale/MerchandisingFormLocale'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { MerchandisingI18nFormFieldContainer } from 'domains/merchandising/components/i18n-form-field-container/MerchandisingI18nFormFieldContainer'
import { getTimezoneOptions } from 'domains/merchandising/utils/heroBannerUtils'
import {
  type HeroBannerFormData,
  type HeroBannerFormProps,
  type I18nHeroBannerFormData,
} from 'domains/merchandising/types/heroBanner.types'
import { isTextBanner } from 'domains/merchandising/utils/heroBannerUtils'
import { PlacementPlatformEnum, ButtonVariantEnum } from 'domains/merchandising/api'
import { useLocales } from 'domains/merchandising/utils/useLocales.hooks'
import { removeLocaleKeys } from 'domains/merchandising/utils/i18n'
import { MerchandisingFormActions } from 'domains/merchandising/components/form-actions/MerchandisingFormActions'

const outerContainer = css({
  alignItems: 'center',
  flexDirection: 'column',
  display: 'flex',
  flex: '1',
  position: 'relative',
  width: '100%',
})

const innerContainer = css({
  width: '100%',
})

// If no value exists (create), set the defaultValue to the user's computer/browser timezone
// or fallback to EST (-4) as a last resort.
const currentTimezone = moment.utc().parseZone().utcOffset() / 60 || -4
const defaultTimezone = getTimezoneOptions().find(tz => tz.offset === currentTimezone)?.value

const emptyInitialState: HeroBannerFormData = {
  type: null,
  name: '',
  platform: [
    PlacementPlatformEnum.Mobile,
    PlacementPlatformEnum.Web,
    PlacementPlatformEnum.Ios,
    PlacementPlatformEnum.Android,
  ],
  destination: {
    type: null,
    target: '',
    displayValue: null,
  },
  scheduling: {
    startDate: new Date().toISOString(),
    endDate: null,
    timezone: defaultTimezone,
  },
  headingText: {
    color: '#000000',
    text: '',
  },
  subheadingText: {
    color: '#000000',
    text: '',
  },
  descriptionText: {
    color: '#000000',
    text: '',
  },
  button: {
    styleVariant: ButtonVariantEnum.Primary,
    label: '',
  },
  backgroundColor: '#FFFFFF',
  imageAltText: '',
  images: {
    large: '',
    small: '',
  },
  banners: [],
}

const i18nPaths = [
  'id',
  'headingText.color',
  'headingText.text',
  'subheadingText.color',
  'subheadingText.text',
  'descriptionText.color',
  'descriptionText.text',
  'button.styleVariant',
  'button.label',
  'backgroundColor',
  'imageAltText',
  'images.large',
  'images.small',
]

export const HeroBannerForm: FunctionComponent<HeroBannerFormProps> = ({
  type,
  initialState: initialStateProp = emptyInitialState,
  submitError,
  isSubmitting,
  onCancel,
  onFormSubmit,
}) => {
  const locales = useLocales()

  const initialState = useMemo(
    () =>
      i18nPaths.reduce(
        (state, path) =>
          set(
            state,
            path,
            Object.fromEntries(
              locales.map(locale => [
                locale,
                get(
                  initialStateProp.banners.find(
                    ({ locale: bannerLocale }) => bannerLocale === locale
                  ),
                  path,
                  get(initialStateProp, path === 'id' ? 'bannerId' : path)
                ),
              ])
            )
          ),
        // @ts-expect-error, these types don't seem to overlap that much... TODO: fix me, domain owner :)
        {
          ...cloneDeep(omit(initialStateProp, ['banners'])),
          locale: locales[0],
        } as I18nHeroBannerFormData
      ),
    [initialStateProp, locales]
  )

  // TODO: remove this once it is fixed on the BE
  // Field has not been added to V4 yet, so this unblocks staging/testing
  // In the future, this will be a required field, so it won't be an issue
  const scheduling = {
    ...initialState.scheduling,
    ...(initialState.scheduling.timezone === '' && { timezone: defaultTimezone }),
  }

  // BannerType can be selected in the create form, so if it exists, merge it with the existing defaultValues.
  // Once a banner type has been selected, it can't be edited. Storing it in a hidden field to pass along with the form submission.
  const form = useForm<I18nHeroBannerFormData>({
    mode: 'onBlur',
    defaultValues: { ...initialState, locale: locales[0], type, scheduling },
  })
  const { handleSubmit, register, getValues } = form

  const onSubmit = (i18nFormData: I18nHeroBannerFormData) => {
    const formData: HeroBannerFormData = {
      ...emptyInitialState,
      ...removeLocaleKeys(omit(i18nFormData, ['id', 'locale']), locales[0]),
      banners: locales.map(locale =>
        i18nPaths.reduce(
          (result, path) =>
            setWith(
              result,
              path,
              get(i18nFormData, `${path}.${locale}`, get(emptyInitialState, path)),
              Object
            ),
          { locale }
        )
      ),
    }
    onFormSubmit(formData, form as unknown as FormContextValues<HeroBannerFormData>)
  }

  return (
    <div css={outerContainer} data-testid="heroBanner-form">
      <form css={innerContainer} onSubmit={handleSubmit(onSubmit)}>
        <input name="type" ref={register} type="hidden" />
        <HeroBannerFormDetail form={form} />
        <HeroBannerFormLocation form={form} />
        <HeroBannerFormLinkDestination
          form={form}
          defaultDisplayValue={initialState.destination?.displayValue}
        />
        <MerchandisingFormSection
          titleId="merchandising.hero-banners.section.content.title"
          tooltipId={
            isTextBanner(type)
              ? 'merchandising.hero-banners.section.content.text.tooltip'
              : 'merchandising.hero-banners.section.content.image.tooltip'
          }
          renderAction
        >
          <MerchandisingFormLocale form={form} />
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <>
                <input
                  type="hidden"
                  name={`id.${locale}`}
                  defaultValue={getValues(`id.${locale}`)}
                  ref={register}
                />
              </>
            )}
          />
          {isTextBanner(type) && <HeroBannerFormContentText form={form} />}
          <HeroBannerFormContentImage type={type} form={form} />
        </MerchandisingFormSection>

        <MerchandisingFormActions
          onCancel={onCancel}
          isLoading={isSubmitting}
          submitError={submitError}
        />
      </form>
    </div>
  )
}
